import { useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";

// Acknowledge only a visitor message actually delivered to a visible conversation.
// Never acknowledge "now": that could hide a concurrently arriving unseen message.
export function useLiveMessageRead(sessionId: string | null, messages?: { id: string; sender: string; internal?: boolean }[]) {
  const { user, organization } = useAuth();
  const latest = messages?.filter(m => m.sender === "visitor" && !m.internal).at(-1)?.id;
  useEffect(() => {
    if (!sessionId || !latest) return;
    const read = () => {
      if (document.visibilityState !== "visible" || !document.hasFocus()) return;
      void apiRequest("POST", `/api/live-chat/notifications/${sessionId}/messages/read`, { messageId: latest })
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/notifications"] });
          if (typeof BroadcastChannel !== "undefined") {
            const channel = new BroadcastChannel(`live-chat-read:${organization?.id}:${user?.id}`);
            channel.postMessage("read"); channel.close();
          }
        })
        .catch(() => { /* Remain unread on failure; polling retries. */ });
    };
    read();
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", read);
    const timer = setInterval(read, 15000);
    return () => {
      clearInterval(timer); window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", read);
    };
  }, [sessionId, latest, organization?.id, user?.id]);
}
